import { StrictMode, Component, ErrorInfo, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { ThemeProvider } from "./contexts/ThemeContext";
import "./index.css";
import "mapbox-gl/dist/mapbox-gl.css";

// ─── Boot-time env diagnostic ─────────────────────────────────────────────────
declare const __VITE_MAPBOX_ACCESS_TOKEN__: string;
declare const __VITE_API_BASE_URL__: string;
{
  // Try every possible source. Vite's `define` config injects build-time string
  // constants that bypass `import.meta` entirely, which has been broken at
  // runtime in this project for unknown reasons.
  let raw: any = undefined;
  try { raw = (0, eval)("import.meta"); } catch { /* ignore */ }
  const env: any = raw?.env || (import.meta as any)?.env || {};
  const viteKeys = Object.keys(env).filter((k) => k.startsWith("VITE_"));
  const importMetaToken = env.VITE_MAPBOX_ACCESS_TOKEN;
  const defineToken = (() => {
    try { return typeof __VITE_MAPBOX_ACCESS_TOKEN__ === "string" ? __VITE_MAPBOX_ACCESS_TOKEN__ : ""; }
    catch { return ""; }
  })();
  const defineApiBase = (() => {
    try { return typeof __VITE_API_BASE_URL__ === "string" ? __VITE_API_BASE_URL__ : ""; }
    catch { return ""; }
  })();
  const resolvedToken = importMetaToken || defineToken;
  const hasToken = typeof resolvedToken === "string" && resolvedToken.length > 10;
  /* eslint-disable no-console */
  console.group(
    "%c[boot] env diagnostic",
    `color:${hasToken ? "#065F46" : "#9F1239"};font-weight:bold;background:${hasToken ? "#D1FAE5" : "#FEE2E2"};padding:3px 8px;border-radius:4px;font-size:12px;`
  );
  console.log("typeof import.meta:", typeof raw, " · keys:", raw ? Object.keys(raw) : "n/a");
  console.log("import.meta.url:", raw?.url);
  console.log("MODE:", env.MODE, " · DEV:", env.DEV, " · PROD:", env.PROD);
  console.log("VITE_* keys (import.meta.env):", viteKeys);
  console.log(
    "Token via import.meta.env (masked):",
    typeof importMetaToken === "string" && importMetaToken.length > 10
      ? `${importMetaToken.slice(0, 12)}…${importMetaToken.slice(-6)}`
      : "<MISSING>"
  );
  console.log(
    "Token via define (__VITE_MAPBOX_ACCESS_TOKEN__) masked:",
    defineToken && defineToken.length > 10
      ? `${defineToken.slice(0, 12)}…${defineToken.slice(-6)}`
      : "<MISSING>"
  );
  console.log("API base via define (__VITE_API_BASE_URL__):", defineApiBase || "<MISSING>");
  console.log("Resolved token present?", hasToken);
  console.groupEnd();
  /* eslint-enable no-console */
}
// ──────────────────────────────────────────────────────────────────────────────

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("App error boundary caught:", error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <div style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          fontFamily: "system-ui, sans-serif",
          background: "linear-gradient(135deg,#1D3047,#254874)",
          color: "white",
        }}>
          <div style={{
            maxWidth: 520,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 16,
            padding: "2rem",
            textAlign: "center",
          }}>
            <h1 style={{ fontSize: 22, marginBottom: 8 }}>Something went wrong</h1>
            <p style={{ fontSize: 14, opacity: 0.8, marginBottom: 16 }}>
              {this.state.error.message || "An unexpected error occurred."}
            </p>
            <button
              onClick={this.handleReload}
              style={{
                background: "#E3B02E",
                color: "#1D3047",
                border: "none",
                padding: "10px 18px",
                borderRadius: 8,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auto-recover from stale lazy-chunk 404s after a Vercel redeploy.
// When a user has an old tab open and navigates to a route whose chunk
// has been replaced by a new build, Vite emits 'vite:preloadError'.
window.addEventListener("vite:preloadError", () => {
  if (!sessionStorage.getItem("ss-chunk-reloaded")) {
    sessionStorage.setItem("ss-chunk-reloaded", "1");
    window.location.reload();
  }
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>
);
